import { useMemo, useRef, useState, useEffect, useCallback } from "react";
import styled from "styled-components";

// #region SCOPED CSS
const StyledPage = styled.div`
    .item-variation-component-wrapper {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(316px, 1fr));
        gap: 10px;
    }
`;

const StyledVariationCard = styled.div`
    &.variation-card {
        background: var(--backgrounds-and-lines-n-300, #1a222d);
        border-radius: 4px;
        border: 1px solid var(--backgrounds-and-lines-n-100, #3c485a);
        padding: 16px;
        display: flex;
        flex-direction: column;
        gap: 10px;
        min-height: 160px;
    }

    /* --- Header: color swatch + name + code --- */
    .variation-header {
        display: flex;
        align-items: center;
        gap: 10px;
    }
    .color-swatch {
        border-radius: 4px;
        flex-shrink: 0;
        width: 38px;
        height: 38px;
    }
    .header-info {
        display: flex;
        flex-direction: column;
        gap: 10px;
        min-width: 0;
    }
    .color-name {
        color: #ffffff;
        font-size: var(--overline-font-size, 14px);
        letter-spacing: var(--overline-letter-spacing, 0.015em);
        font-weight: var(--overline-font-weight, 500);
        text-transform: uppercase;
        line-height: 1.2;
    }
    .color-code {
        color: #ffffff;
        font-size: var(--subtitle-2-font-size, 12px);
        font-weight: var(--subtitle-2-font-weight, 400);
        line-height: 1.2;
    }

    &[disabled] {
        .color-name {
            color: var(--text-and-icons-gray-300, #4f5b6d);
        }
        .color-code {
            color: var(--text-and-icons-gray-300, #4f5b6d);
        }
        .column-label {
            color: var(--text-and-icons-gray-300, #4f5b6d);
        }
    }

    /* --- Data columns layout --- */
    .data-columns {
        display: flex;
        gap: 10px;
    }
    .data-column {
        display: flex;
        flex-direction: column;
        gap: 4px;
        flex-grow: 1;
    }

    /* --- Column header row (label + line) --- */
    .column-header {
        display: flex;
        align-items: center;
        gap: 10px;
    }
    .column-label {
        color: var(--text-and-icons-gray-100, #d1d1d1);
        font-size: var(--label-xs-uppercase-font-size, 8px);
        letter-spacing: var(--label-xs-uppercase-letter-spacing, 0.0125em);
        font-weight: var(--label-xs-uppercase-font-weight, 500);
        text-transform: uppercase;
        white-space: nowrap;
    }
    .column-line {
        border-top: 1px solid var(--backgrounds-and-lines-n-100, #3c485a);
        flex: 1;
        height: 0;
    }

    /* --- Cell values --- */
    .cell-value {
        color: #ffffff;
        font-size: var(--subtitle-3-font-size, 10px);
        font-weight: var(--subtitle-3-font-weight, 400);
        line-height: 1.3;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .cell-spacer {
        line-height: 1.3;
        font-size: var(--subtitle-3-font-size, 10px);
        visibility: hidden;
    }
`;
// #endregion

export default function ItemVariationsSection({ productData, pageEnabled }) {
    // #region REFS
    const gridRef = useRef(null);
    // #endregion

    // #region STATE
    const [disabledCount, setDisabledCount] = useState(0);
    // #endregion

    // #region METHODS
    const getColorVariations = () => {
        const colorVariations = productData?.colors || [];
        const colorVariationsOrdered = colorVariations?.reduce((acc, item) => {
            const colorCode = item?.colorCode || item?.hexacode;
            const colorName = item?.name;
            if (!acc[colorCode]) {
                acc[colorCode] = { colorName, hexacode: item?.hexacode, items: [] };
            }
            // Add each size as an item
            item?.sizes?.forEach(size => {
                acc[colorCode].items.push({
                    size: size?.size,
                    barcode: size?.barcode,
                    sku: size?.sku,
                    weight: size?.weight
                });
            });
            /*const randomNumberBetween1to10 = Math.floor(Math.random() * 10) + 1;
            for (let i = 0; i < randomNumberBetween1to10; i++) {
                acc[colorCode].items.push({
                    size: `Size ${i}`,
                    barcode: `Barcode ${i}`
                });
            }*/
            return acc;
        }, {}); // Order color variations by colorCode for display

        const colorVariationsArray =
            colorVariationsOrdered &&
            Object.entries(colorVariationsOrdered).map(([colorCode, { colorName, hexacode, items }]) => ({
                colorCode,
                colorName,
                hexacode,
                items
            })); // Map colors to gtins format for backward compatibility with existing components

        return colorVariationsArray;
    };
    // #endregion

    // #region USE MEMO
    const colorVariations = useMemo(() => getColorVariations(), [productData]);
    const maxItemCount = useMemo(
        () => colorVariations?.reduce((max, v) => Math.max(max, v?.items?.length || 0), 0) || 0,
        [colorVariations]
    );
    // #endregion

    // #region CALLBACKS
    const calculateDisabledCount = useCallback(() => {
        const grid = gridRef.current;
        if (!grid) return;

        const gridWidth = grid.clientWidth;
        const gap = 10;
        const minCardWidth = 316;

        // Calculate how many columns fit: same formula CSS auto-fill uses
        const columns = Math.max(1, Math.floor((gridWidth + gap) / (minCardWidth + gap)));
        const totalCards = colorVariations?.length || 0;
        const remainder = totalCards % columns;
        const fillCount = remainder === 0 ? 0 : columns - remainder;

        setDisabledCount(fillCount);
    }, [colorVariations]);
    // #endregion

    // #region USE EFFECTS
    useEffect(() => {
        const grid = gridRef.current;
        if (!grid) return;

        // Calculate on mount and whenever colorVariations changes
        calculateDisabledCount();

        // Recalculate on resize using ResizeObserver
        const resizeObserver = new ResizeObserver(() => {
            calculateDisabledCount();
        });
        resizeObserver.observe(grid);

        return () => resizeObserver.disconnect();
    }, [calculateDisabledCount]);
    // #endregion

    return (
        <StyledPage
            className={pageEnabled ? "" : "d-none"}
            style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
            <div className="card-title" style={{ alignSelf: "flex-start" }}>
                ITEM VARIATIONS
            </div>

            <div className="item-variation-component-wrapper" ref={gridRef}>
                {colorVariations?.map((variation, index) => (
                    <NewItemVariationComponent key={index} variation={variation} maxItemCount={maxItemCount} />
                ))}
                {Array.from({ length: disabledCount }, (_, index) => (
                    <NewItemVariationComponent
                        key={`disabled-${index}`}
                        maxItemCount={maxItemCount}
                        disabledVersion={true}
                    />
                ))}
            </div>
        </StyledPage>
    );
}

// #region COMPONENTS
const COLUMNS = [
    { key: "size", label: "SIZE", width: 45 },
    { key: "barcode", label: "EAN CODE", width: 74 },
    { key: "sku", label: "SKU", width: 104 },
    { key: "weight", label: "WEIGHT", width: 31 }
];

const NewItemVariationComponent = ({ variation, maxItemCount, disabledVersion = false }) => {
    const colorName = disabledVersion ? "-" : variation?.colorName;
    const bgColor = disabledVersion
        ? "var(--backgrounds-lines-n-600)"
        : `#${variation?.hexacode || variation?.colorCode}`;
    const colorCode = disabledVersion ? "-" : variation?.colorCode;
    const items = disabledVersion ? [] : variation?.items;
    const spacerCount = Math.max(0, maxItemCount - (items?.length || 0));

    return (
        <StyledVariationCard className="variation-card" disabled={disabledVersion}>
            {/* Header */}
            <div className="variation-header">
                <div className="color-swatch" style={{ backgroundColor: bgColor }} />
                <div className="header-info">
                    <div className="color-name">{colorName ?? "-"}</div>
                    <div className="color-code">{colorCode ?? "-"}</div>
                </div>
            </div>

            {/* Data columns */}
            <div className="data-columns">
                {COLUMNS.map(col => (
                    <div className="data-column" key={col.key} style={{ minWidth: col.width }}>
                        <div className="column-header">
                            <span className="column-label">{col.label}</span>
                            <div className="column-line" />
                        </div>
                        {items?.map((item, idx) => (
                            <div className="cell-value" key={idx}>
                                {item?.[col.key] ?? "-"}
                            </div>
                        ))}
                        {Array.from({ length: spacerCount }, (_, idx) => (
                            <div className="cell-spacer" key={`spacer-${idx}`}>
                                 
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </StyledVariationCard>
    );
};
// #endregion
